"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Product } from "@/types/models/product";
import { getProducts, searchProducts, getProduct } from "@/store/actions/products.actions";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Search, Check, Package } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProductSelectorProps {
  label?: string;
  value?: string[];
  onValueChange: (productIds: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  maxSelections?: number;
  multiSelect?: boolean;
  /** Products from parent (e.g. collection GET) to show labels for selected IDs before opening the dropdown */
  seedProducts?: Product[];
}

interface ProductOption {
  id: string;
  title: string;
  price: number;
  image?: string;
  status: string;
  variantCount: number;
}

const productToOption = (product: Product): ProductOption => ({
  id: product.id!,
  title: product.title,
  price: product.variants?.[0]?.price ?? 0,
  image: product.images?.[0],
  status: product.status,
  variantCount: product.variants?.length ?? 0,
});

const mergeProductOptions = (existing: ProductOption[], incoming: ProductOption[]): ProductOption[] => {
  const byId = new Map<string, ProductOption>();
  for (const p of existing) byId.set(p.id, p);
  for (const p of incoming) {
    byId.set(p.id, p);
  }
  return Array.from(byId.values());
};

const loadingPlaceholder = (id: string): ProductOption => ({
  id,
  title: "Loading…",
  price: 0,
  image: undefined,
  status: "ACTIVE",
  variantCount: 0,
});

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  label = "Select Products",
  value = [],
  onValueChange,
  placeholder = "Search and select products",
  error,
  helperText,
  maxSelections = 10,
  multiSelect = true,
  seedProducts,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const didLoadInitialCatalogRef = useRef(false);
  const resolveAttemptedRef = useRef<Set<string>>(new Set());

  /** Chips + labels: derived from controlled `value` + loaded `products` (avoids stale effect ordering vs seed). */
  const displaySelected = useMemo(() => {
    return value.map((id) => {
      const p = products.find((x) => x.id === id);
      return p ?? loadingPlaceholder(id);
    });
  }, [value, products]);

  const loadInitialProducts = async () => {
    try {
      setLoading(true);
      const response = await getProducts({ 
        page: 1, 
        pageSize: 10,
        filters: { status: "ACTIVE" } 
      });
      if (response.success && response.data) {
        const productOptions: ProductOption[] = response.data.map(productToOption);
        setProducts((prev) => mergeProductOptions(prev, productOptions));
      }
    } catch (error) {
      console.error("[ProductSelector] Failed to load initial products", error);
    } finally {
      setLoading(false);
    }
  };

  // Hydrate options from parent (e.g. collection detail `products[]`) so selected chips show without opening
  useEffect(() => {
    if (!seedProducts?.length) return;
    const incoming = seedProducts.map(productToOption);
    setProducts((prev) => mergeProductOptions(prev, incoming));
  }, [seedProducts]);

  // Fetch any selected IDs not yet in `products` (e.g. featured metafield set but collection GET omitted `products`)
  useEffect(() => {
    const missing = value.filter((id) => !products.some((p) => p.id === id));
    const toResolve = missing.filter((id) => !resolveAttemptedRef.current.has(id));
    if (!toResolve.length) return;

    toResolve.forEach((id) => resolveAttemptedRef.current.add(id));
    let cancelled = false;

    void (async () => {
      const results = await Promise.all(
        toResolve.map(async (id) => {
          try {
            const res = await getProduct({ id });
            const data = res?.data as Product | undefined;
            return data?.id ? productToOption(data) : null;
          } catch (e) {
            console.warn("[ProductSelector] getProduct failed", { id, e });
            return null;
          }
        }),
      );
      if (cancelled) return;

      const opts: ProductOption[] = toResolve.map((id, i) => {
        const got = results[i];
        if (got) return got;
        return {
          id,
          title: "Unavailable",
          price: 0,
          image: undefined,
          status: "INACTIVE",
          variantCount: 0,
        };
      });
      setProducts((prev) => mergeProductOptions(prev, opts));
    })();

    return () => {
      cancelled = true;
    };
  }, [value, products]);

  // First time the dropdown opens, merge first page of catalog (seed may already have rows)
  useEffect(() => {
    if (!open || didLoadInitialCatalogRef.current) return;
    didLoadInitialCatalogRef.current = true;
    void loadInitialProducts();
  }, [open]);

  const searchProductsApi = async (query: string) => {
    if (!query.trim()) {
      loadInitialProducts();
      return;
    }

    try {
      setLoading(true);
      // Use real search API endpoint
      const response = await searchProducts({
        query: query,
        page: 1,
        pageSize: 20,
        status: "ACTIVE",
      });

      if (response.success && response.data) {
        const productOptions: ProductOption[] = response.data.map(productToOption);
        setProducts((prev) => mergeProductOptions(prev, productOptions));
      }
    } catch (error) {
      console.error("Failed to search products:", error);
      // Fallback to filter existing products if search fails
      const filtered = products.filter((product) =>
        product.title.toLowerCase().includes(query.toLowerCase()),
      );
      setProducts(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      searchProductsApi(query);
    }, 300);
  };

  const handleProductSelect = (product: ProductOption) => {
    if (!multiSelect) {
      onValueChange([product.id]);
      setOpen(false);
      setSearchQuery("");
      return;
    }

    const isSelected = value.includes(product.id);
    let newSelection: string[];

    if (isSelected) {
      newSelection = value.filter((id) => id !== product.id);
    } else {
      if (value.length >= maxSelections) {
        return;
      }
      newSelection = [...value, product.id];
    }

    onValueChange(newSelection);
  };

  const handleRemoveProduct = (
    productId: string,
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();
    onValueChange(value.filter((id) => id !== productId));
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex-1 text-left">
                {displaySelected.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {displaySelected.slice(0, 3).map((product) => (
                      <span
                        key={product.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                      >
                        <Package size={10} />
                        {product.title.length > 15
                          ? `${product.title.slice(0, 15)}...`
                          : product.title}
                        {multiSelect && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => handleRemoveProduct(product.id, e)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleRemoveProduct(product.id, e);
                              }
                            }}
                            className="ml-1 text-primary/60 hover:text-primary text-xs cursor-pointer"
                            aria-label={`Remove ${product.title}`}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    ))}
                    {displaySelected.length > 3 && (
                      <span className="text-xs text-muted-foreground px-1.5 py-1">
                        +{displaySelected.length - 3} more
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-secondary-background text-foreground border border-border rounded-b-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4),0_4px_6px_-2px_rgba(0,0,0,0.25)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.8),0_4px_6px_-2px_rgba(0,0,0,0.6)]"
            align="start"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <div className="flex items-center border-b border-border px-3 bg-secondary-background">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div 
              className="max-h-[300px] overflow-y-auto overflow-x-hidden bg-secondary-background rounded-b-xl" 
              style={{ scrollBehavior: 'smooth' }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              {multiSelect && value.length >= maxSelections && (
                <div className="p-2 text-center text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20">
                  Maximum {maxSelections} products can be selected
                </div>
              )}

              <div className="p-1 flex flex-col gap-[5px]">
                {loading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Loading products...
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {searchQuery
                      ? "No products found."
                      : "No products available."}
                  </div>
                ) : (
                  products.map((product) => {
                    const isSelected = value.includes(product.id);
                    return (
                      <div
                        key={product.id}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                          isSelected && "bg-accent text-accent-foreground",
                        )}
                        onClick={() => handleProductSelect(product)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 text-primary",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.title}
                              className="w-8 h-8 rounded object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                              <Package
                                size={14}
                                className="text-muted-foreground"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">
                              {product.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-muted-foreground">
                                ₹{(product.price / 100).toFixed(2)}
                              </p>
                              <span className="text-xs text-muted-foreground">
                                •
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {product.variantCount} variant
                                {product.variantCount !== 1 ? "s" : ""}
                              </p>
                              <span
                                className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                  product.status === "ACTIVE"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                                    : product.status === "DRAFT"
                                      ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
                                )}
                              >
                                {product.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {multiSelect && value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length}/{maxSelections} products selected
        </p>
      )}

      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export default ProductSelector;
